import { useCallback, useEffect, useMemo } from 'react';
import intl from 'react-intl-universal';
import { Typography } from '@getsynapse/design-system';
import useModal from 'Hooks/useModal';

interface HideDashboardModalProps {
  handleHide: () => void;
  onCloseModal: () => void;
  shouldDisplayModal: boolean;
  dashboardName: string;
  isLoading?: boolean;
}

const HideDashboardModal = ({
  handleHide,
  onCloseModal,
  shouldDisplayModal,
  dashboardName,
  isLoading = false,
}: HideDashboardModalProps) => {
  const { Modal, modalProps, openModal, closeModal } = useModal();

  const handleCloseModal = useCallback(() => {
    onCloseModal();
    closeModal();
  }, [closeModal, onCloseModal]);

  const confirmHideDashboard = useCallback(() => {
    handleHide();
    handleCloseModal();
  }, [handleCloseModal, handleHide]);

  useEffect(() => {
    if (shouldDisplayModal && !modalProps.isOpen) {
      openModal(modalProps);
    }
  }, [modalProps, openModal, shouldDisplayModal]);

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('DASHBOARDS_LIST_PAGE.TABLE.ACTION.HIDE'),
        variant: 'primary',
        onClick: confirmHideDashboard,
        'data-testid': 'confirm-button',
        loading: isLoading,
        disabled: isLoading,
      },
      {
        children: intl.get('CANCEL'),
        variant: 'tertiary',
        onClick: handleCloseModal,
        'data-testid': 'cancel-button',
        disabled: isLoading,
      },
    ],
    [confirmHideDashboard, handleCloseModal, isLoading]
  );

  return (
    <Modal
      {...modalProps}
      title={intl.get('HIDE_DASHBOARD_MODAL.TITLE')}
      titleIcon={{
        name: 'eye-off-outline',
        className: 'text-primary-dark',
      }}
      size='small'
      aria-label={intl.get('HIDE_DASHBOARD_MODAL.TITLE')}
      childrenClassName='flex flex-col space-y-6'
      data-testid='hide-dashboard-modal'
      closeModal={handleCloseModal}
      actionButtons={actionButtons}
    >
      <>
        <Typography variant='body' data-testid='hide-caption'>
          {intl.get('HIDE_DASHBOARD_MODAL.CAPTION', {
            name: dashboardName,
          })}
          <br />
          {intl.get('HIDE_DASHBOARD_MODAL.SUB_CAPTION')}
        </Typography>
      </>
    </Modal>
  );
};

export default HideDashboardModal;
